import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import bacService from '../../services/bacStudent.service';
import type { BacPreconisations } from '../../services/bacStudent.service';

const pts = (centiemes: number) => (centiemes / 100).toFixed(2).replace('.', ',');
const num = { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', px: 0.75 } as const;

/** Savoir-faire non cochés du plus rentable au moins rentable, avec le cumul des points à partir de la note actuelle. */
export default function PreconisationsTab({ studentId }: { studentId: number }) {
  const [data, setData] = useState<BacPreconisations | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<number | null>(null);

  const load = useCallback(
    () =>
      bacService
        .getPreconisations(studentId)
        .then(setData)
        .catch(() => setError('Erreur lors du chargement des préconisations')),
    [studentId]
  );

  useEffect(() => {
    load();
  }, [load]);

  // cocher un savoir-faire le retire de la liste et change la note : on recharge le tableau
  const master = async (skillId: number) => {
    setSaving(skillId);
    try {
      await bacService.setMastery(studentId, skillId, true);
      await load();
    } catch {
      setError("Impossible d'enregistrer, réessaie.");
    } finally {
      setSaving(null);
    }
  };

  if (!data && !error) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {data && (
        <>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Note actuelle : {data.note.toFixed(2).replace('.', ',')} / 20
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Les savoir-faire à travailler en priorité : ceux qui rapportent le plus de points au bac (en moyenne sur les
            sujets de bac de sa série). La note visée est un ordre de grandeur : une question ne rapporte ses points que si tous
            ses savoir-faire sont acquis.
          </Typography>
          {data.skills.length === 0 ? (
            <Typography>Tous les savoir-faire qui rapportent des points sont cochés.</Typography>
          ) : (
            <Table size="small" sx={{ '& td, & th': { verticalAlign: 'top' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ px: 0.5 }} />
                  <TableCell sx={{ px: 0.75 }}>Savoir-faire</TableCell>
                  <TableCell sx={num}>Points</TableCell>
                  <TableCell sx={num}>Cumul</TableCell>
                  <TableCell sx={num}>Note visée</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.skills.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell sx={{ px: 0.5, py: 0 }}>
                      <Tooltip title="Marquer comme acquis">
                        <span>
                          <Checkbox
                            size="small"
                            checked={false}
                            disabled={saving !== null}
                            onChange={() => master(s.id)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ px: 0.75 }}>
                      <Typography variant="body2">{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.chapitre} · {s.groupe}
                      </Typography>
                    </TableCell>
                    <TableCell sx={num}>{pts(s.centiemes)}</TableCell>
                    <TableCell sx={num}>{pts(s.cumul)}</TableCell>
                    <TableCell sx={{ ...num, fontWeight: 600 }}>{s.note_visee.toFixed(2).replace('.', ',')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Box>
  );
}
